import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useObserver } from 'mobx-react-lite';

import { useModals, ModalNames } from 'components/modals';
import useUser from 'hooks/use-user';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { useSelfServePhoneVerificationRollout } from 'src/hooks/use-self-serve-phone-verification-rollout';
import { usePhoneVerificationNudgeAnalytics } from 'src/hooks/use-phone-verification-nudge-analytics';
import { usePhoneVerification } from 'src/components/modals/phone-verification-nudge/use-phone-verification';
import { parseSendStatus } from 'src/components/modals/phone-verification-nudge/phone-verification.types';
import type { OtpHostContext, OtpHostOutcome } from '../phone-verification-cta/otp-host';

/**
 * FINTECH-4845. Data/gating hook for the NET-NEW, self-serve "Verify" button rendered next to
 * the Mobile Phone field on My Account -- deliberately separate from `usePhoneVerificationCta`
 * (the pre-existing "Verify Now" banner's own hook), which is NOT touched by this feature at
 * all. Per the human design decision: the two entry points must stay fully independent, each
 * behind its own gate, rather than sharing a composed gate. Some duplication with the banner
 * hook's shape is the deliberate cost of that isolation.
 *
 * Gate is the self-serve rollout flag ALONE -- no OR with the legacy nudge killswitch. Default
 * OFF (rollout dial), so this hook is a true no-op (no network call, isVisible always false)
 * until a dispensary/enterprise is explicitly targeted.
 *
 * VISIBILITY IS PROFILE-DERIVED, NOT STATUS-DERIVED (human design decision, follow-up to the
 * initial FINTECH-4845 ship): `leg_one` is derived directly from the user's own profile (`phone`
 * present, `phoneVerifiedAt` absent), so this standalone self-serve entry point needs no upfront
 * status request. The rare "verified on another account" case is
 * therefore handled REACTIVELY off the send/verify round-trip itself (see `onVerifyClick`
 * below), not from an upfront status check.
 */
export type SelfServeCtaState =
  | {
      kind: 'verified_other';
      dispensaryId?: string | null;
      enterpriseId?: string | null;
      ownerEmailHint?: string | null;
    }
  | { kind: 'leg_one'; dispensaryId?: string | null; enterpriseId?: string | null };

export type SelfServeBannerErrorKind = 'invalid_number' | 'rate_limited' | 'unavailable';

export type SelfServeBannerError = {
  kind: SelfServeBannerErrorKind;
};

export type UseSelfServeVerifyButtonResult = {
  isVisible: boolean;
  isSending: boolean;
  cta: SelfServeCtaState | null;
  bannerError: SelfServeBannerError | null;
  onVerifyClick: () => Promise<void>;
};

type SendPayload =
  | {
      status?: string | null;
      phoneHint?: string | null;
      errorCode?: string | null;
    }
  | null
  | undefined;

function mapSendFailure(payload: SendPayload): SelfServeBannerError {
  if (payload?.errorCode === 'rate_limited') {
    return { kind: 'rate_limited' };
  }
  if (payload?.errorCode === 'invalid_number' || payload?.errorCode === 'undeliverable') {
    return { kind: 'invalid_number' };
  }
  return { kind: 'unavailable' };
}

// Forces the button hidden (`'hidden'`) or into the reactive `verified_other` informational row,
// overriding the profile-derived default. `null` means "no override -- use the profile-derived
// state". Cleared whenever the underlying profile/dispensary context actually changes (see the
// effect below), so a stale override never outlives the real-world state it was standing in for.
type CtaOverride = SelfServeCtaState | 'hidden' | null;

export function useSelfServeVerifyButton(): UseSelfServeVerifyButtonResult {
  const { openModal } = useModals();
  const user = useUser();
  const isLoggedIn = useObserver(() => user.exists);
  const phone = useObserver(() => user.profile?.phone);
  // Derived live from the profile on every read (mirrors `User#phoneVerifiedAt`'s own doc
  // comment) so a revocation or a fresh verification is reflected on the very next profile
  // refetch -- never cached/derived once at mount.
  const phoneVerifiedAt = useObserver(() => user.phoneVerifiedAt);
  const { dispensary } = useDispensary() as {
    dispensary?: { id?: string | null; retailer?: { enterpriseId?: string | null } | null };
  };
  const dispensaryId = dispensary?.id ?? null;
  const enterpriseId = dispensary?.retailer?.enterpriseId ?? null;
  const { sendCode } = usePhoneVerification();
  // The ONLY gate -- no legacy-killswitch OR. Default false everywhere until a
  // dispensary/enterprise is explicitly targeted; see the hook's own doc comment.
  const rolloutEnabled = useSelfServePhoneVerificationRollout();
  // Own analytics surface (`myAccountSelfServeButton`), never `myAccountBanner` -- see
  // FINTECH-4845 in events.ts for why this must stay a distinct funnel.
  const analytics = usePhoneVerificationNudgeAnalytics({ surface: 'myAccountSelfServeButton' });

  const [isSending, setIsSending] = useState(false);
  const [bannerError, setBannerError] = useState<SelfServeBannerError | null>(null);
  const [ctaOverride, setCtaOverride] = useState<CtaOverride>(null);
  const previousCtaKindRef = useRef<SelfServeCtaState['kind'] | null>(null);
  // Adversarial-review finding (addressed): unlike the always-mounted legacy banner, THIS
  // component can be unmounted mid-click by a live rollout-flag flip to off (my-account.jsx
  // suppresses it and remounts the banner instead) -- a race the banner structurally cannot
  // have. Guards the click continuation from resuming (opening the OTP modal, touching state)
  // after this specific unmount path, without pretending to fix the (separate, pre-existing,
  // inherited-from-the-banner) logout/navigation races this hook intentionally still shares.
  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );
  // Adversarial-review finding (addressed): dispensaryId/enterpriseId are ordinary render-scope
  // consts, so an in-flight onVerifyClick's closure keeps referencing whatever they were AT
  // CLICK TIME even after a re-render changes them -- comparing `cta.dispensaryId` against a
  // same-closure `dispensaryId` inside that same async function is comparing a value to itself,
  // never actually detecting that the real, current context has since diverged. This ref is the
  // one thing that keeps updating every render regardless of which closure reads it, so the
  // conflict-outcome branch below can tell click-time context apart from resume-time context.
  const liveContextRef = useRef({ dispensaryId, enterpriseId });
  liveContextRef.current = { dispensaryId, enterpriseId };

  // No-op (hidden, no network call) when logged out or the flag is off -- a true no-op, matching
  // the hook's own gate doc comment. Otherwise, `leg_one` iff the profile has a phone on file
  // and it isn't already verified.
  const derivedCta = useMemo<SelfServeCtaState | null>(() => {
    if (!isLoggedIn || !rolloutEnabled) {
      return null;
    }
    if (!phone || phoneVerifiedAt) {
      return null;
    }
    return { kind: 'leg_one', dispensaryId, enterpriseId };
  }, [isLoggedIn, rolloutEnabled, phone, phoneVerifiedAt, dispensaryId, enterpriseId]);

  // A real change to any of the profile-derived inputs supersedes whatever the override was
  // standing in for (an immediate post-click hide/conflict projection) -- drop it and let the
  // freshly-derived state take over. Also clears any stale bannerError from a previous click,
  // mirroring the banner hook's own "a fresh read supersedes a stale UI-only error" posture.
  useEffect(() => {
    setCtaOverride(null);
    setBannerError(null);
  }, [isLoggedIn, rolloutEnabled, phone, phoneVerifiedAt, dispensaryId, enterpriseId]);

  const cta = ctaOverride === 'hidden' ? null : ctaOverride ?? derivedCta;

  useEffect(() => {
    if (cta?.kind && previousCtaKindRef.current !== cta.kind) {
      analytics.track('shown', { leg: cta.kind === 'leg_one' ? 'leg_one' : 'leg_two' });
    }
    previousCtaKindRef.current = cta?.kind ?? null;
  }, [cta?.kind, analytics]);

  const onVerifyClick = useCallback(async (): Promise<void> => {
    if (cta?.kind !== 'leg_one' || isSending) {
      return;
    }

    analytics.trackVerifyClicked();
    setIsSending(true);
    try {
      const result = await sendCode(cta.dispensaryId, cta.enterpriseId);
      if (!mountedRef.current) {
        // Unmounted (rollout flag flipped off) while the send was in flight -- the legacy
        // banner may already have taken this surface's place. Do not touch state or open a
        // modal for a component that's gone.
        return;
      }
      const payload = result.sendPhoneVerificationCode;

      if (parseSendStatus(payload?.status) !== 'sent') {
        const failure = mapSendFailure(payload);
        analytics.trackFailed(payload?.errorCode ?? undefined);
        setBannerError(failure);
        return;
      }

      analytics.trackCodeSent();
      setBannerError(null);

      const context: OtpHostContext = {
        phoneHint: payload?.phoneHint ?? null,
        dispensaryId: cta.dispensaryId,
        enterpriseId: cta.enterpriseId,
      };

      setIsSending(false);

      // Reuses the EXISTING OTP modal (same ModalNames.phoneVerificationOtp host the banner
      // opens) -- no new OTP flow.
      const outcome = (await openModal<OtpHostContext, OtpHostOutcome>(ModalNames.phoneVerificationOtp, context)) as
        | OtpHostOutcome
        | undefined;

      // False positive below: the lint rule can't see that mountedRef.current is mutated by the
      // unmount cleanup effect (an external ref write), asynchronously, between this read and
      // the one above.
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (!mountedRef.current) {
        // Same unmounted-mid-flight guard, now after the OTP round-trip itself.
        return;
      }

      if (outcome?.kind === 'verified') {
        analytics.trackCompleted();
        // Immediate UI feedback -- hide now rather than wait for `user.refetch()`'s data to
        // land and `phoneVerifiedAt` to flow back through the profile-derived projection above.
        setCtaOverride('hidden');
        user.refetch();
        return;
      }

      if (outcome?.kind === 'conflict') {
        // REACTIVE handling of the rare "verified on another account" case (human design
        // decision, follow-up to the initial ship): no upfront status read backs this button's
        // visibility at all, so there is no "invalidate and let the status effect recompute"
        // move available here, unlike the banner hook's `refreshToken` bump. `ClaimVerifiedPair`
        // (verify_code's own claim step) is what actually detects this, at verify time -- this
        // is not something `send_code` itself can report; the button projects it inline the
        // moment the OTP host resolves with it, with no hint (the wire response carries none).
        analytics.trackFailed('conflict');
        // Adversarial-review finding (addressed): the dispensary/enterprise context can change
        // while the OTP modal is open (e.g. cross-tab navigation) -- the context-change effect
        // above already resets ctaOverride once for that, but if the outcome resolves AFTER that
        // reset, unconditionally re-applying it here with the STALE dispensaryId/enterpriseId
        // captured at click-time would install a mismatched override that then persists
        // indefinitely (nothing clears it again until the context changes AGAIN). Compare
        // against `liveContextRef` (updated every render), NOT another render-scope const --
        // comparing two values pinned to the SAME click-time closure would always agree with
        // itself and never catch a real divergence. Only apply the override when the click's
        // captured context still matches the CURRENT one; otherwise the context-change effect
        // has already superseded this outcome and there is nothing correct left to project.
        if (
          cta.dispensaryId === liveContextRef.current.dispensaryId &&
          cta.enterpriseId === liveContextRef.current.enterpriseId
        ) {
          setCtaOverride({
            kind: 'verified_other',
            dispensaryId: cta.dispensaryId,
            enterpriseId: cta.enterpriseId,
            ownerEmailHint: null,
          });
        }
        return;
      }

      if (outcome?.kind === 'unavailable') {
        analytics.trackFailed();
        setBannerError({ kind: 'unavailable' });
        return;
      }

      analytics.trackDismissed();
    } catch {
      analytics.trackFailed();
      setBannerError({ kind: 'unavailable' });
    } finally {
      setIsSending(false);
    }
  }, [analytics, cta, isSending, sendCode, openModal, user]);

  return useMemo(
    () => ({ isVisible: cta !== null || bannerError !== null, isSending, cta, bannerError, onVerifyClick }),
    [cta, bannerError, isSending, onVerifyClick]
  );
}
